import interpolateComponents from '@automattic/interpolate-components';
import { parseJSON, subMonths } from 'date-fns';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { sprintf } from 'sprintf-js';

import { getSynonymSearchApiKey } from 'api/getSynonymSearchApiKey';
import { Box } from 'components/Box/Box';
import { Button } from 'components/Button/Button';
import { getCompensationSurveyFields } from 'components/Form/Fields/CompensationSurveyFields';
import { Form } from 'components/Form/Form';
import type { FormSubmitFunction } from 'components/Form/FormContext';
import { MultiStepForm } from 'components/Form/MultiStepForm/MultiStepForm';
import { Icon } from 'components/Icon/Icon';
import { StyledNativeLinkWithRef } from 'components/Link/StyledNativeLinkWithRef';
import { LoadingDots } from 'components/LoadingDots/LoadingDots';
import { IdealistLogo } from 'components/Logo/IdealistLogo';
import { SuccessCheckmark } from 'components/SuccessCheckmark/SuccessCheckmark';
import { Body } from 'components/Text/Body';
import { BodyMicro } from 'components/Text/BodyMicro';
import { BodySmall } from 'components/Text/BodySmall';
import { H4 } from 'components/Text/H4';
import { Text } from 'components/Text/Text';
import { Underline } from 'components/Text/Underline';
import { useUserEnvironment } from 'contexts/UserEnvironmentContext';
import { useSetDocumentTitle } from 'hooks/useSetDocumentTitle';
import { salariesFetchReport } from 'modules/salaries/api/salariesFetchReport';
import { salariesGetCompensationSurvey } from 'modules/salaries/api/salariesGetCompensationSurvey';
import { salariesSubmitCompensationSurvey } from 'modules/salaries/api/salariesSubmitCompensationSurvey';
import { salariesValidateCompensationSurvey } from 'modules/salaries/api/salariesValidateCompensationSurvey';
import {
  exploreSalaryRoute,
  salarySurveyFaqRoute,
  salarySurveyLearnMoreRoute,
} from 'modules/salaries/routing/salaries.routes';
import type { SalariesCompensationSurveyData } from 'modules/salaries/types/SalariesCompensationSurveyData';
import { trackEvent } from 'utils/analytics/track/trackEvent';
import { now } from 'utils/date';
import { getStaticImageSrc } from 'utils/getImageSrc';
import { getItem, setItem } from 'utils/localStorage';
import { saveTemporaryStorage } from 'utils/temporaryStorage';
import { getParam } from 'utils/url/getParam';
import { salarySurveyLastSubmittedDate } from 'utils/userEnvironment';

function Footer({
  loggedOut,
  isStepZero,
}: {
  loggedOut?: boolean;
  isStepZero?: boolean;
}) {
  return !loggedOut ? (
    <>
      {isStepZero ? (
        <Text styleAs="bodySmall">
          {interpolateComponents({
            mixedString:
              /* TRANSLATORS: Salary survey footer */
              getText(
                `Want to learn more about Idealist's Nonprofit Salary Explorer? {{link}}Click here to learn more.{{/link}}`,
              ),
            components: {
              link: (
                <StyledNativeLinkWithRef
                  target="_blank"
                  href={salarySurveyFaqRoute.fullPath}
                />
              ),
            },
          })}
        </Text>
      ) : null}
      <Text styleAs="bodySmall" mt="1em">
        <Text styleAs="h5" tag="span">
          {
            /* TRANSLATORS: Salary survey footer */
            getText('We take data security very seriously. ')
          }
        </Text>
        {interpolateComponents({
          mixedString:
            /* TRANSLATORS: Salary survey footer */
            getText(
              `{{link}}Check out our FAQ{{/link}} to learn about how we protect your privacy.`,
            ),
          components: {
            link: (
              <StyledNativeLinkWithRef
                target="_blank"
                href={salarySurveyFaqRoute.fullPath}
              />
            ),
          },
        })}
      </Text>
    </>
  ) : (
    <div>
      {isStepZero ? (
        <Text styleAs="bodySmall">
          {interpolateComponents({
            mixedString:
              /* TRANSLATORS: Salary survey footer */
              getText(
                `Want to learn more about Idealist's Nonprofit Salary Explorer? {{link}}Click here to learn more.{{/link}}`,
              ),
            components: {
              link: (
                <StyledNativeLinkWithRef
                  target="_blank"
                  href={salarySurveyFaqRoute.fullPath}
                />
              ),
            },
          })}
        </Text>
      ) : null}

      <Text styleAs="bodySmall" mt="1em">
        <Text styleAs="h5" tag="span">
          {
            /* TRANSLATORS: Salary survey footer */
            getText('We take data security very seriously. ')
          }
        </Text>
        <span>
          {getText(`The information you enter in the survey will not be linked to your
          user account, and will be completely anonymized. `)}
        </span>
        {
          /* TRANSLATORS: Salary survey footer */
          getText('Want to know more? ')
        }
        {interpolateComponents({
          mixedString:
            /* TRANSLATORS: Salary survey footer */
            getText(
              `{{link}}Check out our FAQ{{/link}} to learn about how we protect your privacy.`,
            ),
          components: {
            link: (
              <StyledNativeLinkWithRef
                target="_blank"
                href={salarySurveyFaqRoute.fullPath}
              />
            ),
          },
        })}
      </Text>
    </div>
  );
}

function Loading() {
  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      flexDirection="column"
      minHeight={300}
    >
      <LoadingDots />
    </Box>
  );
}

const getSteps = ({
  alreadySubmitted,
  userEnvironment,
  titleChanged,
  surveyApiKey,
}: // eslint-disable-next-line @typescript-eslint/no-explicit-any
any) => {
  const userName = userEnvironment.user
    ? userEnvironment.user.firstName || userEnvironment.user.name
    : null;
  let title;

  if (userName) {
    if (alreadySubmitted) {
      title = sprintf(getText('Welcome back, %(userName)s!'), {
        userName,
      });
    } else {
      title = sprintf(getText('Welcome, %(userName)s!'), {
        userName,
      });
    }
  } else if (alreadySubmitted) {
    title = getText('Welcome back!');
  } else {
    title = getText('Welcome!');
  }

  const stepZero = {
    hideNextStep: alreadySubmitted && titleChanged === false,
    submitIsDisabled: alreadySubmitted && titleChanged === null,
    loadingSection:
      !userEnvironment.isUserLoaded || !surveyApiKey ? <Loading /> : null,
    title,
    eventName: 'Salary Survey Start Screen',
    eventProperties:
      userEnvironment.user && alreadySubmitted
        ? {
            already_submitted: alreadySubmitted,
          }
        : undefined,
    nextText:
      alreadySubmitted === false ? getText(`That's me! Let's go!`) : null,
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line no-nested-ternary
    leftButtons: alreadySubmitted ? (
      titleChanged === false ? (
        <Button
          type="link"
          variant="primary"
          size="large"
          data-qa-id="view-salary-report"
          href={exploreSalaryRoute.fullPath}
        >
          {getText("Let's Go!")}

          <Box display="inline-block" ml={12}>
            <Icon name="arrow-right" size={18} />
          </Box>
        </Button>
      ) : null
    ) : (
      <Button
        type="link"
        variant="secondary"
        href={salarySurveyLearnMoreRoute.fullPath}
        data-qa-id="learn-more-button"
        size="large"
      >
        {getText("That's Not Me")}
      </Button>
    ),
    sections: [
      {
        title,
        intro:
          // TODO: Fix this the next time the file is edited.
          // eslint-disable-next-line no-nested-ternary
          alreadySubmitted === null ? (
            <Loading />
          ) : alreadySubmitted === false ? (
            <Body>
              {interpolateComponents({
                mixedString:
                  getText(`We're currently accepting survey data from people who have
          worked {{u}}full time (35+ hours/week) at a nonprofit{{/u}} in the U.S.
          within the last three years.`),
                components: {
                  u: <Underline />,
                },
              })}
            </Body>
          ) : (
            <Body>
              {getText(
                'Have there been any significant changes since you last told us about your job?',
              )}
            </Body>
          ),
        rows: alreadySubmitted
          ? [
              {
                fields: ['titleChanged'],
              },
            ]
          : [],
      },
    ],
    ignoreInNav: true,
    footer: <Footer loggedOut={!userEnvironment.user} isStepZero />,
  };
  const otherSteps = [
    {
      title: getText('You and Your Job'),
      eventName: 'Your Job',
      nextText: getText('Continue'),
      sections: [
        {
          title: getText('You and Your Job'),
          intro: (
            <>
              <BodySmall>
                {getText(
                  'Tell us about you! Please provide the required information (marked with an *) below.',
                )}
              </BodySmall>

              <BodySmall mt="8px">
                {interpolateComponents({
                  mixedString:
                    /* TRANSLATORS: Salary survey footer */
                    getText(
                      `A reminder: your information will be anonymized and kept completely confidential. Check out our {{link}}FAQ{{/link}} to learn more.`,
                    ),
                  components: {
                    link: (
                      <StyledNativeLinkWithRef
                        target="_blank"
                        href={salarySurveyFaqRoute.fullPath}
                      />
                    ),
                  },
                })}
              </BodySmall>
            </>
          ),
          rows: [
            {
              fields: ['jobTitle'],
              fieldsToValidate: ['selectedSynonymId', 'titleSuggestion'],
            },
            {
              fields: ['annualSalary'],
            },
            {
              fields: ['managerialStatus'],
            },
            {
              fields: ['yearsOfExperience'],
            },
            {
              fields: ['educationLevel'],
            },
          ],
        },
      ],
      footer: <Footer />,
    },
    {
      title: getText('Your Employer'),
      eventName: 'Your Employer',
      nextText: getText('Continue'),
      sections: [
        {
          title: getText('Your Employer'),
          rows: [
            {
              fields: ['location'],
              fieldsToValidate: [
                'remoteStatus',
                'salaryBasedOn',
                'workCity',
                'workStateCode',
                'workLatitude',
                'workLongitude',
                'employerCity',
                'employerStateCode',
                'employerLatitude',
                'employerLongitude',
              ],
            },
            {
              fields: ['employerOrgType'],
            },
            {
              fields: ['employerOrgSubType'],
            },
            {
              fields: ['employerOrgDescriptor'],
              fieldsToValidate: [],
            },
            {
              fields: ['employerNEmployeesBucket', null],
            },
          ],
        },
      ],
      footer: <Footer />,
    },
    {
      title: getText('Equity and Inclusion'),
      eventName: 'Equity & Inclusion',
      nextText: getText('Submit'),
      sections: [
        {
          title: getText('Equity and Inclusion'),
          intro: (
            <>
              <BodySmall mb={15}>
                {interpolateComponents({
                  mixedString:
                    getText(`While the optional questions below are personal in
                    nature, rest assured that Idealist will anonymize
                    your answers and protect your data. Visit our
                    {{link}}FAQ{{/link}} to learn more.
                    `),
                  components: {
                    link: (
                      <StyledNativeLinkWithRef
                        target="_blank"
                        href={salarySurveyFaqRoute.fullPath}
                      />
                    ),
                  },
                })}
              </BodySmall>
              <BodySmall>
                {getText(`With your participation, you have the potential to
                impact and influence pay equity in our sector.`)}
              </BodySmall>
            </>
          ),
          rows: [
            {
              fields: ['gender'],
            },
            {
              fields: ['genderFreeText'],
            },
            {
              fields: ['race'],
            },
            {
              fields: ['raceFreeText'],
            },
            {
              fields: ['ageBucket', null],
            },
            {
              fields: ['disabilityStatus'],
            },
            {
              fields: ['militaryServiceStatus'],
            },
          ],
        },
      ],
      footer: <Footer />,
    },
  ];
  // @ts-expect-error TS(2769): No overload matches this call.
  return [stepZero].concat(otherSteps);
};

const getValuesToValidate = (
  values: Record<string, unknown>,
  validate: string[],
) =>
  Object.fromEntries(
    Object.entries(values).filter(([k, _v]) =>
      [...validate, 'publish'].includes(k),
    ),
  );

function Success() {
  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      py={96}
      textAlign="center"
    >
      <SuccessCheckmark containerSize={64} iconSize={32} />

      <H4 mb={64}>{getText('Thanks for your input!')}</H4>

      <LoadingDots />

      <BodySmall mt={24}>{getText('Securely submitting your data')}</BodySmall>
    </Box>
  );
}

export function CompensationSurvey() {
  const navigate = useNavigate();
  const location = useLocation();
  const userEnvironment = useUserEnvironment();
  const [surveyApiKey, setSurveyApiKey] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [alreadySubmitted, setAlreadySubmitted] = useState(null);
  const oneMonthAgo = subMonths(new Date(), 1);
  const submissions = getItem('salarySurveyLastSubmitted');
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let submittedTooManyTimes: any;

  if (submissions && Array.isArray(submissions)) {
    submittedTooManyTimes =
      submissions.filter((dateStr) => parseJSON(dateStr) > oneMonthAgo)
        .length >= 5;
  }

  const lastSubmitted = salarySurveyLastSubmittedDate(userEnvironment.user);

  useEffect(() => {
    salariesGetCompensationSurvey().then(
      ({ alreadySubmitted: newAlreadySubmitted }) => {
        if (submittedTooManyTimes) {
          navigate(exploreSalaryRoute.fullPath, {
            state: {
              flashMessage: { type: 'SURVEY_SUBMITTED_TOO_MANY_TIMES' },
            },
            replace: true,
          });
        } else {
          // @ts-expect-error TS(2345): Argument of type 'boolean' is not assignable to pa... Remove this comment to see the full error message
          setAlreadySubmitted(newAlreadySubmitted);
        }
        // @ts-expect-error TS(2345): Argument of type 'boolean' is not assignable to pa... Remove this comment to see the full error message
        setAlreadySubmitted(Boolean(getItem('salarySurveyLastSubmitted')));
      },
    );
  }, [navigate, submittedTooManyTimes]);

  useEffect(() => {
    getSynonymSearchApiKey().then((newApiKey) => {
      // @ts-expect-error TS(2345): Argument of type 'string' is not assignable to par... Remove this comment to see the full error message
      setSurveyApiKey(newApiKey);
    });
  }, []);

  useSetDocumentTitle('Idealist');

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const updateCurrentStep = (step: any) => {
    setCurrentStep(step);
    window.scrollTo(0, 0);
    window.dispatchEvent(new Event('resize'));
  };

  const goBackToStep = useMemo(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    () => (step: any) => {
      updateCurrentStep(step);
    },
    [],
  );
  const initialValues = useMemo(
    () => ({
      titleSuggestion: '',
      selectedSynonymId: null,
      managerialStatus: false,
      yearsOfExperience: null,
      educationLevel: '',
      ageBucket: '',
      employerBudgetBucket: '',
      employerOrgType: '',
      employerNEmployeesBucket: '',
      remoteStatus: '',
      salaryBasedOn: '',
      workCity: '',
      workStateCode: '',
      workGeoType: '',
      employerCity: '',
      employerStateCode: '',
      annualSalary: '',
      disabilityStatus: null,
      militaryServiceStatus: null,
      publish: false,
      nextStep: 1,
      validate: [],
      titleChanged: null,
      // does not submit to server
      // TODO: Remove when testing is done.
      source: getParam(location, 'test') ? 'TEST' : 'MAIN_SURVEY',
    }),
    [location],
  );

  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line react/function-component-definition, react/no-unstable-nested-components
  const CompensationSurveyForm = ({
    values,
    updateValueWith,
  }: {
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    values: Record<string, any>;
    updateValueWith: (
      arg0: string | string[],
      arg1: (arg0: unknown) => unknown,
    ) => void;
  }) => {
    if (!surveyApiKey) {
      return <Loading />;
    }

    const fields = getCompensationSurveyFields(
      // @ts-expect-error TS(2345): Argument of type '(arg0: string | string[], arg1: ... Remove this comment to see the full error message
      updateValueWith,
      lastSubmitted,
      surveyApiKey,
    );
    const steps = getSteps({
      alreadySubmitted,
      userEnvironment,
      redirectTo: `${location.pathname}${location.search}`,
      titleChanged: values.titleChanged,
      surveyApiKey,
    });

    return (
      <MultiStepForm
        // @ts-expect-error TS(2322): Type '{ hideNextStep: any; submitIsDisabled: any; ... Remove this comment to see the full error message
        steps={steps}
        currentStep={currentStep}
        fields={fields}
        goBackToStep={goBackToStep}
        hasTracking
        logo={
          <StyledNativeLinkWithRef href="/">
            <IdealistLogo withText height={52} />
          </StyledNativeLinkWithRef>
        }
        successSection={submitted ? <Success /> : null}
        sidebar={[
          {
            imageUrl: getStaticImageSrc('compdb/survey-step1.svg'),
            width: 270,
            body: (
              <BodyMicro px="48px">
                {getText(
                  'After answering a few questions, you’ll gain access to our salary calculator, a powerful tool to support you as you try to negotiate pay, switch roles, or relocate to a new city.',
                )}
              </BodyMicro>
            ),
          },
          {
            imageUrl: getStaticImageSrc('compdb/survey-step2.svg'),
            width: 320,
          },
          {
            imageUrl: getStaticImageSrc('compdb/survey-step0.svg'),
            width: 270,
          },
          {
            imageUrl: getStaticImageSrc('compdb/survey-step3.svg'),
            width: 310,
          },
        ]}
      />
    );
  };

  const addSubmissionToLocalStorage = () => {
    const newSubmissionDate = now();

    if (submissions && Array.isArray(submissions)) {
      setItem('salarySurveyLastSubmitted', [...submissions, newSubmissionDate]);
    } else {
      setItem('salarySurveyLastSubmitted', [newSubmissionDate]);
    }
  };

  const onSubmit: FormSubmitFunction<SalariesCompensationSurveyData> = ({
    values,
    handleError,
    updateSubmitting,
  }) => {
    const source = getParam(location, 'test') ? 'TEST' : 'MAIN_SURVEY';
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line no-param-reassign
    values.source = source;
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const valuesToValidate: Record<string, any> = values.validate
      ? getValuesToValidate(values, values.validate)
      : [];

    if (values.nextStep) {
      salariesValidateCompensationSurvey(valuesToValidate, source === 'TEST')
        .then(() => {
          updateCurrentStep(currentStep + 1);
          updateSubmitting(false);
        })
        .catch(handleError);
    } else {
      salariesSubmitCompensationSurvey(values)
        .then(() => {
          trackEvent('Success: Submitted Survey');
          addSubmissionToLocalStorage();
          setSubmitted(true);

          if (!userEnvironment.user) {
            // only needed for google/facebook, but save anyway
            return saveTemporaryStorage([
              {
                type: 'SURVEY_REPORT_DATA',
                data: {
                  jobTitleId: values.jobTitleId,
                  jobTitle: values.jobTitle,
                  selectedSynonymId: values.selectedSynonymId,
                  selectedSynonymFamilyId: values.selectedSynonymFamilyId,
                  city: values.workCity || undefined,
                  stateCode: values.workStateCode || undefined,
                  cityStateUnified:
                    values.workCity && values.workStateCode
                      ? `${values.workCity}, ${values.workStateCode}`
                      : undefined,
                  latitude: values.workLatitude,
                  longitude: values.workLongitude,
                  yearsOfExperience: values.yearsOfExperience,
                  geoType: values.workGeoType,
                },
              },
            ]).then((tmpId) => {
              window.location.href = `${exploreSalaryRoute.fullPath}?tmpId=${tmpId}`;
            });
          }

          setTimeout(() => {
            salariesFetchReport({
              jobTitle: '',
              selectedSynonymId: values.selectedSynonymId,
              city: values.workCity,
              stateCode: values.workStateCode,
              latitude: values.workLatitude,
              longitude: values.workLongitude,
              yearsOfExperience: values.yearsOfExperience,
            })
              .then((calculatorResult) => {
                navigate(exploreSalaryRoute.fullPath, {
                  state: {
                    calculatorResult,
                    initialValues: {
                      jobTitle: values.jobTitle,
                      jobTitleId: values.jobTitleId,
                      selectedSynonymId: values.selectedSynonymId,
                      selectedSynonymFamilyId: values.selectedSynonymFamilyId,
                      cityStateUnified:
                        values.workCity && values.workStateCode
                          ? `${values.workCity}, ${values.workStateCode}`
                          : undefined,
                      city: values.workCity || undefined,
                      stateCode: values.workStateCode || undefined,
                      latitude: values.workLatitude,
                      longitude: values.workLongitude,
                      yearsOfExperience: values.yearsOfExperience,
                      geoType: values.workGeoType,
                    },
                  },
                });
              })
              .catch(handleError)
              .finally(() => updateSubmitting(false));
          }, 750);
          return null;
        })
        .catch(handleError);
    }
  };

  return (
    <Form
      analyticsTitle="[salaries] Compensation Survey"
      usageContext="page"
      data-qa-id="compensation-survey-form"
      // @ts-expect-error fix types
      initialValues={initialValues}
      onSubmit={onSubmit}
      // @ts-expect-error fix types
      render={CompensationSurveyForm}
    />
  );
}
